<template>
  <div class="gs-asset-type">
    <div class="gs-asset-split">
      <template v-for="(swipe, index) in swipes">
        <section v-if="isSwipesTabActive(swipe)" :key="swipe.id" class="gs-asset-editor">
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="swipe.name"
                  :validator="$v.localFunnelResource.swipes.$each[index].name"
                  :attribute="$t('frontend_groovesell_swipe_name')"
                  :placeholder="$t('frontend_name')"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="swipe.enabled" :value="swipe.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-swipe"
                title="Delete swipe"
                aria-label="Delete swipe"
                @click="openConfirmModal(index)"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <span class="gs-asset-editor__label">{{ $t('frontend_resources_subject') }}</span>
            <div
              v-for="(subject, subjectIndex) in swipe.subjects"
              :key="subjectIndex"
              class="gs-asset-editor__subject"
            >
              <div class="gs-asset-editor__subject-input">
                <text-input
                  v-model="swipe.subjects[subjectIndex]"
                  :validator="$v.localFunnelResource.swipes.$each[index].subjects.$each[subjectIndex]"
                  :attribute="$t('frontend_resources_subject')"
                  :placeholder="$t('frontend_resources_subject')"
                />
              </div>
              <button
                v-if="swipe.subjects.length > 1"
                type="button"
                class="gs-asset-editor__icon-btn"
                data-action="delete-subject"
                title="Remove subject line"
                aria-label="Remove subject line"
                @click="deleteSubject(index, subjectIndex)"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
            <button
              type="button"
              class="gs-asset-editor__add-subject"
              data-action="add-subject"
              @click="addSubject(index)"
            >
              <plus-icon class="w-3 h-3 fill-current" />
              <span>Add subject line</span>
            </button>
          </div>

          <div class="gs-asset-editor__section">
            <span class="gs-asset-editor__label">{{ $t('frontend_resources_body') }}</span>
            <gs-rich-text-editor
              v-model="swipe.htmlBody"
              :html-view.sync="isSwipesPageHTMLView"
              :variables="variableMenu"
              :upload-url="uploadUrl"
              :placeholder="$t('frontend_resources_body')"
            />
          </div>

          <div class="gs-asset-editor__section">
            <gs-field label="Plain-text version">
              <div class="gs-swipe-variable-row" role="group" aria-label="Plain-text variables">
                <span>Insert a variable at your cursor.</span>
                <gs-name-select
                  class="gs-swipe-insert"
                  placeholder="Insert variable"
                  max-height="min(280px, 40vh)"
                  :actions="textInsertActions(index)"
                  size="action"
                />
              </div>
              <textarea
                :ref="`plainBody${index}`"
                v-model="swipe.textBody"
                class="gs-asset-editor__textarea gs-swipe-plain-text"
                aria-label="Plain-text version"
                :placeholder="$t('frontend_resources_text_body')"
              ></textarea>
            </gs-field>
          </div>
        </section>
      </template>

      <!-- Only shown when there is something to pick: with an empty list the
           list's own empty state is the single message. -->
      <div v-if="hasSwipes && !selectedSwipe" class="gs-asset-empty">
        <p>Select a swipe from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Swipes"
        :items="swipes"
        :selected-id="selectedSwipeId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled swipe"
        :empty-copy="$t('frontend_resources_you_currently')"
        enabled-key="enabled"
        :error-ids="swipesErrorIds"
        @select="selectSwipe"
        @add="createAffiliateSwipe()"
        @reorder="reorderSwipes"
      />
    </div>

    <gs-alert-dialog
      v-model="confirmOpen"
      title="Delete this swipe?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="deleteSwipe()"
    />
  </div>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import generateNumberMixin from '@/mixins/generateNumberMixin';
import GsRichTextEditor from '@/components/editor/GsRichTextEditor.vue';
import { buildVariableMenu, insertTokenAtSelection } from '@/components/editor/editorVariables';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import PlusIcon from '@/assets/images/icons/PlusIcon.svg';
import AssetItemList from './AssetItemList.vue';
import { toTabOrder, sortByTabOrder } from './tabOrder';

export default {
  mixins: [generateNumberMixin],
  components: {
    AssetItemList,
    GsRichTextEditor,
    TrashIcon,
    PlusIcon,
  },
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    swipesTab: {
      type: String,
      default: '',
    },
    uploadUrl: {
      type: String,
      default: '',
    },
    landingPages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      swpiteId: null,
      confirmOpen: false,
      localFunnelResource: this.funnelResource,
      localSwipesTab: this.swipesTab,
      isSwipesPageHTMLView: false,
    }
  },
  computed: {
    hasSwipes() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.swipes &&
        this.localFunnelResource.swipes.length
      );
    },
    swipes() {
      return (this.localFunnelResource && this.localFunnelResource.swipes) || [];
    },
    selectedSwipe() {
      return this.swipes.filter((swipe) => this.isSwipesTabActive(swipe))[0] || null;
    },
    selectedSwipeId() {
      if (!this.localSwipesTab) return null;
      return this.localSwipesTab.replace('swipe_tab_', '');
    },
    variableMenu() {
      // The Insert menu wears the same two labels the plain-text Insert
      // dropdowns beside it do, so both editors name the tokens identically.
      return buildVariableMenu(this.landingPages, {
        labels: {
          affiliateName: this.$t('frontend_resources_add_affiliate_name'),
          affiliateLink: this.$t('frontend_resources_add_affiliate_link'),
        },
      });
    },
    swipesErrorIds() {
      let swipesErrorIds = [];

      let swipesIndex;
      for (swipesIndex in this.$v.localFunnelResource.swipes.$each.$iter) {
        if (this.$v.localFunnelResource.swipes.$each.$iter[swipesIndex].$error) {
          swipesErrorIds.push(
            this.$v.localFunnelResource.swipes.$each.$iter[swipesIndex].$model.id
          );
        }
      }

      return swipesErrorIds;
    },
  },
  mounted() {
    this.$root.$on('trigger-validation', () => {
      this.$v.$touch();
    });
  },
  methods: {
    affiliateLinksActionsTextView(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToSwipesTextView(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    textInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToSwipesTextView(index),
        },
        ...this.affiliateLinksActionsTextView(index),
      ];
    },
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    selectSwipe(id) {
      this.handleSwipesTabChange(`swipe_tab_${id}`);
    },
    // `AssetItemList` reports the new order as ids; the existing handler speaks
    // the tab shape gd-tabs used, so the ids are wrapped back into it.
    reorderSwipes(ids) {
      this.handleSwipesTabOrderUpdate(toTabOrder('swipe', ids));
    },
    handleSwipesTabChange(tab) {
      this.localSwipesTab = tab || this.localSwipesTab;
      this.$emit('handleSwipesTabChange', this.localSwipesTab);
    },
    handleSwipesTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.swipes, 'swipe', updatedTabs);

      this.handleSwipesTabChange();
      this.updateFunnelResource();
    },
    createAffiliateSwipe() {
      const newSwipeId = this.generateNumber();
      const newSwipeCount = this.localFunnelResource.swipes.length + 1;

      this.localFunnelResource.swipes.push({
        id: newSwipeId,
        name: `Swipe ${newSwipeCount}`,
        subjects: [ `${this.$t('frontend_groovesell_tools_affiliate_edit_swipe_subject')} #1`],
        htmlBody: '',
        textBody: '',
      });

      this.localSwipesTab = `swipe_tab_${newSwipeId}`;
      this.handleSwipesTabChange();
      this.updateFunnelResource();
    },
    openConfirmModal(swpiteId) {
      this.swpiteId = swpiteId;
      this.confirmOpen = true;
    },
    async deleteSwipe() {
      this.localSwipesTab = '';

      this.localFunnelResource.swipes.splice(this.swpiteId, 1);
      if (this.localFunnelResource.swipes.length > 0) {
        let nextOpenIndex = 0;
        if (this.swpiteId == 0) {
          nextOpenIndex = 0;
        } else {
          nextOpenIndex = this.swpiteId - 1;
        }

        const nextOpenId = this.localFunnelResource.swipes[nextOpenIndex].id;
        this.localSwipesTab = `swipe_tab_${nextOpenId}`;
      }
      this.updateFunnelResource();
      this.handleSwipesTabChange();
    },
    addSubject(index) {
      const newSubjectCount =
        this.localFunnelResource.swipes[index].subjects.length + 1;

      this.localFunnelResource.swipes[index].subjects.push(
        `${this.$t('frontend_groovesell_tools_affiliate_edit_swipe_subject')} #${newSubjectCount}`
      );
      this.updateFunnelResource();
    },
    deleteSubject(index, subjectIndex) {
      this.localFunnelResource.swipes[index].subjects.splice(subjectIndex, 1);
      this.updateFunnelResource();
    },
    insertPlainTextVariable(index, token) {
      const ref = this.$refs[`plainBody${index}`];
      const field = Array.isArray(ref) ? ref[0] : ref;
      const swipe = this.localFunnelResource.swipes[index];
      const result = insertTokenAtSelection(swipe.textBody, token, field && field.selectionStart, field && field.selectionEnd);
      this.$set(swipe, 'textBody', result.text);
      this.updateFunnelResource();
      this.$nextTick(() => {
        if (field) { field.focus(); field.setSelectionRange(result.cursor, result.cursor); }
      });
    },
    addAffiliateNameToSwipesTextView(index) {
      this.insertPlainTextVariable(index, '##affiliateName##');
    },
    addAffiliateLinkToSwipesTextView(index, landingPageId) {
      this.insertPlainTextVariable(index, `##affiliateLink${landingPageId}##`);
    },
    isSwipesTabActive(swipe) {
      return this.localSwipesTab === `swipe_tab_${swipe.id}`;
    },
  },
  validations: {
    localFunnelResource: {
      swipes: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          subjects: {
            $each: {
              required,
              minLength: minLength(1),
              maxLength: maxLength(300),
            },
          },
          $trackBy: 'id',
        },
      },
    },
  },
};
</script>

<style src="./asset-split.css"></style>

<style scoped>
.gs-swipe-variable-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  background: var(--gs-surface-subtle);
  color: var(--gs-muted-foreground);
  font-size: 13px;
  margin-bottom: 8px;
}
.gs-swipe-insert { width: 190px; max-width: 100%; flex: 0 0 auto; }
.gs-swipe-insert ::v-deep .opacity-50 { opacity: 1; color: var(--gs-foreground); }
.gs-swipe-plain-text { display: block; width: 100%; min-height: 260px; box-sizing: border-box; }
</style>
